import { storageService } from './storage.js';
import { supabase } from './supabase.js';
import * as state from './state.js';
import { showMessage } from './utils.js';
import { t } from './i18n.js';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const LAST_SYNC_KEY = 'last_sync_timestamp';

class SyncService {
    _isSyncing = false;
    _syncRequestedWhileRunning = false;

    async run() {
        return false; // V1: Sync disabled
        if (this._isSyncing) {
            console.log('Sync already in progress, queuing next sync...');
            this._syncRequestedWhileRunning = true;
            return;
        }
        
        if (!state.userId) {
            console.warn('Sync skipped: User not logged in.');
            return;
        }

        // 稍微等待，確保本地資料寫入完全穩定
        await new Promise(resolve => setTimeout(resolve, 300));

        this._isSyncing = true;
        console.log('Starting synchronization for user:', state.userId);

        try {
            const syncStartTime = new Date().toISOString();
            
            // 先推：確保本地變更上傳到雲端
            await this.pushChanges();
            
            // 後拉：抓取雲端其他裝置的變更
            // hasNewData 為 true 表示雲端有新資料，需要刷新 UI
            const hasNewData = await this.pullChanges();
            
            await Preferences.set({ key: LAST_SYNC_KEY, value: syncStartTime });
            console.log('Synchronization finished successfully.');
            return hasNewData;
        } catch (error) {
            console.error('Synchronization failed:', error);
            // showMessage(t('syncFailed', { error: error.message }), 'error');
            return false;
        } finally {
            this._isSyncing = false;
            // 檢查是否在同步期間有新的請求
            if (this._syncRequestedWhileRunning) {
                this._syncRequestedWhileRunning = false;
                console.log('Queued sync requested. Waiting 1s before execution...');
                setTimeout(() => {
                    this.run();
                }, 1000);
            }
        }
    }

    async pullChanges() {
        const { value: lastSyncTimestamp } = await Preferences.get({ key: LAST_SYNC_KEY });
        console.log(`Pulling changes from server since ${lastSyncTimestamp || 'the beginning of time'}...`);

        let query = supabase
            .from('transactions')
            .select('*')
            .eq('user_id', state.userId);
        
        if (lastSyncTimestamp) {
            query = query.gt('modified_at', lastSyncTimestamp);
        }

        const { data, error } = await query;

        if (error) {
            throw new Error(`Failed to pull data from Supabase: ${error.message}`);
        }

        if (data && data.length > 0) {
            await storageService.upsertTransactionsFromServer(data);
            console.log(`${data.length} records pulled and upserted locally.`);
            return true;
        } else {
            console.log('No new records to pull from server.');
            return false;
        }
    }

    async pushChanges() {
        console.log('Pushing local changes to server...');
        const unsynced = await storageService.getUnsyncedTransactions(state.userId);
        console.log(`Found ${unsynced.length} unsynced records in local storage.`);

        if (unsynced.length === 0) {
            console.log('No local changes to push.');
            return;
        }

        const toCreate = [];
        const toUpdate = [];

        unsynced.forEach(tx => {
            const serverTx = {
                user_id: tx.user_id,
                amount: tx.amount,
                description: tx.description,
                type: tx.type,
                date: tx.date,
                category: tx.category,
                modified_at: new Date().toISOString(), // 強制更新時間戳
                is_deleted: tx.is_deleted === 1 || tx.is_deleted === true,
            };

            if (tx.sync_status === 'new' && !tx.server_id) {
                toCreate.push({ local_id: tx.id, data: serverTx });
            } else if (tx.sync_status === 'modified' || tx.server_id) {
                // 如果有 server_id，則執行更新
                if (tx.server_id) {
                    serverTx.id = tx.server_id; // 必須包含 id 才能觸發 upsert 的更新
                    toUpdate.push(serverTx);
                } else {
                    toCreate.push({ local_id: tx.id, data: serverTx });
                }
            }
        });
        
        const syncedUpdates = [];

        if (toCreate.length > 0) {
            console.log(`Pushing ${toCreate.length} new records...`);
            const createData = toCreate.map(item => item.data);
            const { data: createdData, error } = await supabase
                .from('transactions')
                .insert(createData)
                .select();

            if (error) {
                console.error("Sync Create Error:", error);
                throw new Error(`Failed to push creations: ${error.message}`);
            }

            if (createdData) {
                createdData.forEach((serverTx, index) => {
                    syncedUpdates.push({
                        local_id: toCreate[index].local_id,
                        server_id: serverTx.id,
                    });
                });
            }
        }

        if (toUpdate.length > 0) {
            console.log(`Pushing ${toUpdate.length} updated records...`);
            const { error } = await supabase
                .from('transactions')
                .upsert(toUpdate, { onConflict: 'id' });
            
            if (error) {
                console.error("Sync Update Error:", error);
                throw new Error(`Failed to push updates: ${error.message}`);
            }

            toUpdate.forEach(tx => {
                const localTx = unsynced.find(t => t.server_id === tx.id);
                if (localTx) {
                    syncedUpdates.push({ local_id: localTx.id, server_id: tx.id });
                }
            });
        }
        
        if (syncedUpdates.length > 0) {
            await storageService.markTransactionsAsSynced(syncedUpdates);
            console.log(`${syncedUpdates.length} records marked as synced locally.`);
        }

        console.log(`${unsynced.length} local changes have been pushed.`);
    }
}

export const syncService = new SyncService();
